type Gtag = (command: string, ...args: unknown[]) => void;

/**
 * Fire a Google Analytics 4 event. Consent-safe: `window.gtag` is only defined
 * after the consent manager (@c15t/scripts) loads GA with measurement consent,
 * so this silently no-ops when consent is absent or during SSR.
 */
export function trackEvent(
  name: string,
  params?: Record<string, unknown>
): void {
  if (typeof window === "undefined") {
    return;
  }
  const { gtag } = window as Window & { gtag?: Gtag };
  if (typeof gtag === "function") {
    gtag("event", name, params ?? {});
  }
}

/** Event params for interactions that carry no additional data. */
type NoParams = Record<never, never>;

/**
 * Typed catalogue of the analytics events the site fires. Each key is a GA4
 * event name (snake_case); the value is the shape of its params. Extend this
 * map when instrumenting a new interaction so GA4 reports and funnels stay
 * consistent across the codebase.
 */
export interface AnalyticsEventParams {
  /** A blog article was liked. */
  blog_like: {
    article_id: string;
  };
  /** A blog article was shared to a platform. */
  blog_share: {
    article_id: string;
    platform: string;
  };
  /** The revenue calculator received a meaningful interaction. */
  calculator_used: NoParams;
  /** The visitor sent a message to the AI chatbot. */
  chat_message_sent: NoParams;
  /** A contact form was submitted successfully. */
  contact_submit: {
    source:
      | "hero_modal"
      | "mobile_cta"
      | "cta_section"
      | "intro"
      | "contact_page"
      | "chatbot";
  };
  /** A primary call-to-action was clicked. */
  cta_click: {
    cta: "calendly" | "contact" | "case_study";
    location:
      | "hero"
      | "revenue_calculator"
      | "chatbot"
      | "sticky_bar"
      | "cta_section"
      | "case_study_band"
      | "final_cta";
  };
  /** The site language was switched. */
  language_switch: {
    from_locale: string;
    to_locale: string;
  };
  /** SPA navigation. Fired on client route change (not the initial load). */
  page_view: {
    page_path: string;
    page_location: string;
    page_title: string;
    locale: string;
    country?: string;
  };
  /** A scroll-depth threshold was crossed (once per page). */
  scroll_depth: {
    percent: 25 | 50 | 75 | 100;
    page_path: string;
  };
  /** Time spent on a page, reported when the visitor leaves it. */
  time_on_page: {
    seconds: number;
    page_path: string;
  };
}

/** The conversion surface a contact form was submitted from. */
export type ContactSubmitSource =
  AnalyticsEventParams["contact_submit"]["source"];

/**
 * Typed wrapper over {@link trackEvent}. The event name must be a known key of
 * {@link AnalyticsEventParams} and its params must match that event's shape.
 * Events whose params are empty (e.g. `chat_message_sent`) take the name only.
 */
export function track<K extends keyof AnalyticsEventParams>(
  name: K,
  ...params: keyof AnalyticsEventParams[K] extends never
    ? []
    : [params: AnalyticsEventParams[K]]
): void {
  trackEvent(name, params[0]);
}
